import { useState, useCallback } from 'react';

/**
 * Hook for handling hiring offers
 * @param {Object} options - Configuration options
 * @param {number} options.conversationId - Conversation ID
 * @param {number} options.employeeId - Employee ID
 * @param {Object} options.conversationData - Conversation data (optional)
 * @param {Object} options.otherUserInfo - Other user info (optional)
 * @param {Function} options.onSuccess - Optional callback called after successful hire (receives result data)
 * @param {Function} options.onError - Optional callback called on error (receives error message)
 * @returns {Object} Hiring state and handler function
 */
const useHire = ({ 
  conversationId, 
  employeeId, 
  conversationData, 
  startDate,
  endDate,
  otherUserInfo,
  onSuccess,
  onError,
  jobOfferId: jobOfferIdProp
}) => {
  const [isHiring, setIsHiring] = useState(false);
  const [error, setError] = useState(null);

  const handleHire = useCallback(async () => {
    if (!conversationId || !employeeId) {
      const errorMsg = 'No se puede contratar: faltan datos de la conversación';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return { success: false, error: errorMsg };
    }

    const jobOfferId = conversationData?.jobOfferId ?? jobOfferIdProp ?? null;

    setIsHiring(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/hirings`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId: parseInt(employeeId),
          conversationId: parseInt(conversationId),
          jobOfferId,
          startDate: startDate,
          endDate: endDate,
          period: 30, // Default 30 days
          position: conversationData?.jobOffer?.position || otherUserInfo?.position || null,
          salary: conversationData?.jobOffer?.salary || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const result = { success: true, data };
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'No se pudo enviar la oferta de contratación';
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Error creating hiring offer:', error);
      const errorMessage = 'Error al enviar la oferta de contratación';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsHiring(false);
    }
  }, [conversationId, employeeId, conversationData, jobOfferIdProp, otherUserInfo, onSuccess, onError]);

  return {
    handleHire,
    isHiring,
    error
  };
};

export default useHire;

